import { chemistryMeasurementKey } from "./chemistryMeasurements";
import { orderByMetadata } from "./chemistryMetadata";
import { ChemistryEditorMaterials, ChemistryRowWarnings } from "./types";
import { Stack, useTheme } from "@mui/material";
import {
  SearchChemicalElements,
  SearchMaterialChemistry,
} from "contexts/search/context";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo, useState } from "react";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { MaterialChemistry } from "store/api/generatedApi";
import { MaterialOverridesFilter } from "./MaterialOverridesFilter";
import { MaterialOverrideRow, ScrapChefChemistryComparison } from "./types";
import { useMaterialOverrideColumns } from "./useMaterialOverrideColumns";
import { useMaterialOverrideRows } from "./useMaterialOverrideRows";

export interface MaterialOverridesTableProps {
  materials: ChemistryEditorMaterials;
  chemicalElements: SearchChemicalElements;
  materialChemistry: SearchMaterialChemistry;
  materialChemistryOverrides: MaterialChemistry[];
  showScale: boolean;
  onUpdate: (
    newRow: MaterialOverrideRow,
    oldRow: MaterialOverrideRow
  ) => MaterialOverrideRow;
  onRevert: (row: MaterialOverrideRow) => void;
  scrapchefComparison?: ScrapChefChemistryComparison;
  rowWarnings?: ChemistryRowWarnings;
}

export const MaterialOverridesTable = ({
  materials,
  chemicalElements,
  materialChemistry,
  materialChemistryOverrides,
  showScale,
  onUpdate,
  onRevert,
  scrapchefComparison,
  rowWarnings,
}: MaterialOverridesTableProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();

  const notifyStatus = useNotifyStatus();

  const [selectedChemicalElementId, setSelectedChemicalElementId] = useState<
    number | null
  >(null);

  const chemicalElementOptions = useMemo(() => {
    return orderByMetadata(chemicalElements.byIndex).map((element) => ({
      value: element.id,
      label: `${element.symbol} (${t(element.name)})`,
    }));
  }, [chemicalElements, t]);

  const rows = useMaterialOverrideRows({
    t,
    materials,
    chemicalElements,
    materialChemistry,
    materialChemistryOverrides,
    selectedChemicalElementId,
  });

  const columns = useMaterialOverrideColumns({
    t,
    notifyStatus,
    materialChemistry,
    handleDeleteItem: onRevert,
    scrapchefComparison,
    rowWarnings,
    showScale,
  });

  return (
    <Stack
      sx={{
        gap: 2,
        maxHeight: "100%",
        width: "100%",
      }}
    >
      <MaterialOverridesFilter
        label={t("element")}
        allLabel={t("all")}
        selectedChemicalElementId={selectedChemicalElementId}
        options={chemicalElementOptions}
        onChange={setSelectedChemicalElementId}
      />

      <Stack
        sx={{
          flex: 1,
          overflow: "hidden",
          width: "100%",
        }}
      >
        <HotkeyCompliantDataGrid
          rows={rows}
          columns={columns}
          getRowId={chemistryMeasurementKey}
          processRowUpdate={onUpdate}
          initialState={{
            filter: {
              filterModel: {
                items: [],
                quickFilterExcludeHiddenColumns: true,
              },
            },
          }}
          sx={{
            maxHeight: "100%",
            "& .MuiDataGrid-cell--editing input": {
              textAlign: "right",
              paddingX: 1,
              ...theme.typography.body1Mono,
            },
            ".numeric": {
              display: "flex",
              justifyContent: "flex-end",
              ...theme.typography.body1Mono,
            },
            ".overridden-cell": {
              backgroundColor: theme.palette.warning.light,
              fontWeight: "fontWeightBold",
              "&:hover": {
                backgroundColor: theme.palette.warning.main,
              },
            },
            "& .MuiDataGrid-row": {
              "&[data-row-overridden='true']": {
                backgroundColor: theme.palette.warning.light,
                fontWeight: "fontWeightBold",
                "&:hover": {
                  backgroundColor: theme.palette.warning.main,
                },
              },
            },
          }}
          getRowClassName={(params) =>
            params.row.is_overridden ? "row-overridden" : ""
          }
          cellSelection
          ignoreValueFormatterDuringExport
        />
      </Stack>
    </Stack>
  );
};
